import { Button } from '@mantine/core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FC } from 'react';

import CircularLoader from '@/components/CircularLoader.tsx';
import NullComponent from '@/components/NullComponent.tsx';
import Unavailable from '@/components/Unavailable.tsx';
import Heading4 from '@/components/text/Heading4.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { DAYS_PER_PAGE } from '@/hooks/useTimeslots.ts';

import { WeekAvailability } from '@/models/Availability.ts';
import { DayjsUnit } from '@/models/Dayjs.ts';
import { Option } from '@/models/Option.ts';
import { Timings } from '@/models/doc-cirrus/DCCalendar.ts';
import { AvailabilityAction } from '@/models/metrics.ts';

import { getTimeSlotInfo } from '@/utils/calendarUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

import ButtonSelection from '../ButtonSelection.tsx';

interface Props {
    calendarId?: string;
    isLoading?: boolean;
    availability?: WeekAvailability[];
    startDate: string;
    pageTo: (date: string, action: AvailabilityAction) => void;
    hasSlots?: boolean;
    nextAvailableSlot?: string;
    onSlotSelect: (slot: string) => void;
    selectedSlot?: string;
    timings?: Timings;
}

const TimeslotCalendar: FC<Props> = ({
    calendarId,
    isLoading,
    availability,
    startDate,
    pageTo,
    hasSlots,
    nextAvailableSlot,
    onSlotSelect,
    selectedSlot,
    timings,
}) => {
    const mapSlotsToOptions = (slots: string[], date: string): Option[] => {
        return slots.map((slot) => {
            const slotTime = dayjs(slot).format('HH:mm');
            const slotDate = dayjs(date).toDate();
            const slotInfo = timings
                ? getTimeSlotInfo(slotTime, timings, slotDate)
                : { is_available: true, segments: [] };
            const color = slotInfo.segments.length > 0 ? slotInfo.segments[0].color : undefined;

            return {
                id: `${calendarId}_${slot}`,
                value: slot,
                label: dayjs(slot).format('HH:mm'),
                color: color,
            };
        });
    };

    const handlePreviousPage = (): void => {
        pageTo(dayjs(startDate).subtract(DAYS_PER_PAGE, DayjsUnit.DAY).format('YYYY-MM-DD'), 'previous_page');
    };

    const handleNextPage = (): void => {
        pageTo(dayjs(startDate).add(DAYS_PER_PAGE, DayjsUnit.DAY).format('YYYY-MM-DD'), 'next_page');
    };

    const handleJumpToNextFree = (): void => {
        pageTo(dayjs(nextAvailableSlot).format('YYYY-MM-DD'), 'jump_next_free');
    };

    if (isLoading)
        return (
            <div className="flex h-40 items-center justify-center">
                <CircularLoader />
            </div>
        );

    if (!availability) return <NullComponent />;

    return (
        <div className="mb-8 flex flex-col gap-6">
            <div className="flex gap-6">
                <Heading4 className="flex-grow text-lg">
                    {`${dayjs(startDate).format('D MMM')} bis ${dayjs(startDate)
                        .add(DAYS_PER_PAGE - 1, DayjsUnit.DAY)
                        .format('D MMM')}`}
                </Heading4>
                <Button
                    variant="transparent"
                    className="size-6 border-0 !p-0 text-slate-900 hover:text-slate-900 disabled:bg-transparent disabled:opacity-50"
                    onClick={handlePreviousPage}
                    disabled={dayjs(startDate).isSame(dayjs()) || dayjs(startDate).isBefore(dayjs())}
                >
                    <ChevronLeft />
                </Button>
                <Button
                    variant="transparent"
                    className="size-6 border-0 !p-0 text-slate-900 hover:text-slate-900"
                    onClick={handleNextPage}
                >
                    <ChevronRight />
                </Button>
            </div>
            {!hasSlots && nextAvailableSlot && (
                <div className="flex h-36 flex-col items-center justify-center gap-6">
                    <Paragraph>
                        {`Der nächste verfügbare Termin ist am ${dayjs(nextAvailableSlot).format('dddd, DD. MMMM')}`}
                    </Paragraph>
                    <Button onClick={handleJumpToNextFree} className="uppercase">
                        Nächstes Zeitfenster
                    </Button>
                </div>
            )}
            {!hasSlots && !nextAvailableSlot && (
                <Unavailable text="Für diesen Zeitraum gibt es keine verfügbaren Termine" />
            )}
            {hasSlots &&
                availability.map(({ date, slots }) => (
                    <div className="flex flex-col gap-3" key={`${calendarId}_day_${date}`}>
                        <Paragraph className="text-sm font-medium text-slate-500">
                            {dayjs(date).format('dddd D. MMM')}
                        </Paragraph>
                        {!slots || slots.length === 0 ? (
                            <Unavailable text="Keine verfügbaren Zeitfenster" />
                        ) : (
                            <ButtonSelection
                                onSelect={onSlotSelect}
                                options={mapSlotsToOptions(slots, date)}
                                selected={selectedSlot}
                                allowDeselect
                            />
                        )}
                    </div>
                ))}
        </div>
    );
};

export default TimeslotCalendar;
